/**
 * Kill-cam EXPORT — turns the on-screen cinematic into a shareable file.
 *
 * Each frame it composites the (landscape) game canvas into a VERTICAL 9:16 output canvas — centre-cropped for
 * portrait (iPhone / TikTok / X), with the mood colour-grade, cinematic letterbox, a soft vignette and the
 * seaofrobin.quest watermark (the PNG wordmark when present, text otherwise) baked in — and a `MediaRecorder`
 * records that output canvas' `captureStream` MERGED with an audio track (the highlight music). At the end it
 * offers the native Share sheet (Web Share API with a file) on mobile, a download fallback everywhere else.
 *
 * BAILS GRACEFULLY where `MediaRecorder` / `captureStream` don't exist (many in-app webviews): the caller keeps
 * the on-screen cinematic, the export button just goes disabled with an honest tooltip — the game never breaks.
 *
 * The crop / watermark / mime / grade MATH is pure and unit-tested in replayexport.test.ts; the rest is DOM glue.
 */
import { REPLAY } from '@piratas/protocol';
import type { GradeParams } from './shotdirector.js';

/* --------------------------------- pure helpers (tested) --------------------------------- */

export interface CropRect { sx: number; sy: number; sw: number; sh: number; }
/** Centre-crop rectangle of a source (srcW×srcH) that matches the output aspect (outW:outH), losing the least. */
export function cropRect(srcW: number, srcH: number, outW: number, outH: number): CropRect {
  if (srcW <= 0 || srcH <= 0 || outW <= 0 || outH <= 0) return { sx: 0, sy: 0, sw: Math.max(1, srcW), sh: Math.max(1, srcH) };
  const outAR = outW / outH, srcAR = srcW / srcH;
  if (srcAR > outAR) { // source wider → crop the sides
    const sw = srcH * outAR; return { sx: (srcW - sw) / 2, sy: 0, sw, sh: srcH };
  }
  const sh = srcW / outAR; return { sx: 0, sy: (srcH - sh) / 2, sw: srcW, sh }; // source taller → crop top/bottom
}

export interface WMBox { x: number; y: number; w: number; h: number; textX: number; textY: number; fontPx: number; }
/** Layout for the corner watermark: an optional wordmark image sits bottom-centred with the domain text under it. */
export function watermarkBox(outW: number, outH: number, imgAspect: number | null): WMBox {
  const pad = Math.round(outW * 0.045);
  const fontPx = Math.max(11, Math.round(outW * 0.032));
  const textY = outH - pad - Math.round(fontPx * 0.4);
  const textX = outW / 2;
  const w = Math.round(outW * 0.42), h = imgAspect ? Math.round(w / Math.max(0.05, imgAspect)) : 0;
  const x = (outW - w) / 2, y = textY - fontPx - Math.round(h) - 6;
  return { x, y, w, h, textX, textY, fontPx };
}

/** CSS `filter` string for the mood grade (applied to the drawImage). Warm moods add sepia; cold subtract it. */
export function gradeCss(g: GradeParams): string {
  const sat = g.saturation.toFixed(3), bri = g.brightness.toFixed(3);
  const sepia = g.warm > 0 ? ` sepia(${g.warm.toFixed(3)})` : '';
  return `saturate(${sat}) brightness(${bri}) contrast(1.06)${sepia}`;
}

/** Pick the best recording container: MP4 first (Safari/iOS share-friendly), then VP9 WebM, then any WebM. */
export function chooseMime(isSupported: (m: string) => boolean = defaultIsSupported): string | null {
  const c = ['video/mp4;codecs=h264,aac', 'video/mp4', 'video/webm;codecs=vp9,opus', 'video/webm;codecs=vp8,opus', 'video/webm'];
  for (const m of c) { try { if (isSupported(m)) return m; } catch { /* ignore */ } }
  return null;
}
function defaultIsSupported(m: string): boolean {
  return typeof MediaRecorder !== 'undefined' && typeof MediaRecorder.isTypeSupported === 'function' && MediaRecorder.isTypeSupported(m);
}

/** Whether this environment can record at all (captureStream + MediaRecorder + a usable mime). */
export function exportSupported(): boolean {
  try {
    return typeof MediaRecorder !== 'undefined'
      && typeof HTMLCanvasElement !== 'undefined'
      && typeof HTMLCanvasElement.prototype.captureStream === 'function'
      && chooseMime() !== null;
  } catch { return false; }
}

/* --------------------------------- DOM / recorder glue --------------------------------- */

export class ReplayExport {
  private out: HTMLCanvasElement | null = null;
  private ctx: CanvasRenderingContext2D | null = null;
  private rec: MediaRecorder | null = null;
  private chunks: BlobPart[] = [];
  private mime = 'video/webm';
  private wordmark: HTMLImageElement | null = null;
  private wordmarkTried = false;
  private outW: number = REPLAY.OUT_W; private outH: number = REPLAY.OUT_H;
  recording = false;

  constructor(private game: HTMLCanvasElement) {}

  static supported(): boolean { return exportSupported(); }

  /** Fetch the brand wordmark once (contract path). Missing → text-only watermark, never an error. */
  async loadWordmark(): Promise<void> {
    if (this.wordmarkTried) return; this.wordmarkTried = true;
    await new Promise<void>((resolve) => {
      const img = new Image();
      img.onload = () => { this.wordmark = img; resolve(); };
      img.onerror = () => { this.wordmark = null; resolve(); };
      img.src = '/brand/sor-wordmark.png';
    });
  }

  /** Build the output canvas + recorder. `portrait` picks 9:16 (default) vs 16:9. `audioStream` is merged in.
   *  Returns false (no throw) when recording isn't possible — the caller keeps the on-screen cinematic. */
  begin(portrait: boolean, audioStream: MediaStream | null): boolean {
    if (!ReplayExport.supported()) return false;
    const mime = chooseMime(); if (!mime) return false;
    this.mime = mime;
    this.outW = portrait ? REPLAY.OUT_W : REPLAY.OUT_W_LAND;
    this.outH = portrait ? REPLAY.OUT_H : REPLAY.OUT_H_LAND;
    const out = document.createElement('canvas'); out.width = this.outW; out.height = this.outH;
    const ctx = out.getContext('2d'); if (!ctx) return false;
    this.out = out; this.ctx = ctx; this.chunks = [];
    try {
      const stream = out.captureStream(REPLAY.EXPORT_FPS);
      if (audioStream) for (const tr of audioStream.getAudioTracks()) stream.addTrack(tr);
      const rec = new MediaRecorder(stream, { mimeType: mime, videoBitsPerSecond: REPLAY.BITRATE });
      rec.ondataavailable = (e) => { if (e.data && e.data.size > 0) this.chunks.push(e.data); };
      this.rec = rec; rec.start(); this.recording = true;
      return true;
    } catch { this.rec = null; this.recording = false; return false; }
  }

  /** Composite ONE frame of the game canvas into the output canvas (grade + vignette + letterbox + watermark).
   *  `fade` (0..1) fades the WHOLE frame to black — the clip's closing fade-out, baked into the export. */
  composite(grade: GradeParams, vignette: number, letterboxFrac: number, fade = 0): void {
    const ctx = this.ctx, out = this.out; if (!ctx || !out) return;
    const g = this.game;
    const crop = cropRect(g.width || g.clientWidth, g.height || g.clientHeight, this.outW, this.outH);
    ctx.save();
    ctx.filter = gradeCss(grade);
    try { ctx.drawImage(g, crop.sx, crop.sy, crop.sw, crop.sh, 0, 0, this.outW, this.outH); } catch { /* tainted/lost ctx */ }
    ctx.restore();
    // mood tint (soft-light so it colours without crushing)
    ctx.save();
    ctx.globalCompositeOperation = 'soft-light';
    ctx.globalAlpha = 0.35;
    ctx.fillStyle = `rgb(${Math.round(clampByte(grade.tint[0] * 128))},${Math.round(clampByte(grade.tint[1] * 128))},${Math.round(clampByte(grade.tint[2] * 128))})`;
    ctx.fillRect(0, 0, this.outW, this.outH);
    ctx.restore();
    // vignette
    const v = Math.max(grade.vignette, vignette);
    if (v > 0.01) {
      const grad = ctx.createRadialGradient(this.outW / 2, this.outH * 0.46, this.outH * 0.28, this.outW / 2, this.outH * 0.5, this.outH * 0.72);
      grad.addColorStop(0, 'rgba(0,0,0,0)'); grad.addColorStop(1, `rgba(0,0,0,${(0.75 * v).toFixed(3)})`);
      ctx.fillStyle = grad; ctx.fillRect(0, 0, this.outW, this.outH);
    }
    // cinematic letterbox
    const bar = Math.round(this.outH * letterboxFrac);
    if (bar > 0) { ctx.fillStyle = '#000'; ctx.fillRect(0, 0, this.outW, bar); ctx.fillRect(0, this.outH - bar, this.outW, bar); }
    // watermark (under the closing fade so it dims out with the picture)
    this.drawWatermark(ctx);
    // closing fade-to-black (the owner's beat #5) — baked into the clip over its final seconds
    if (fade > 0.001) { ctx.save(); ctx.globalAlpha = Math.min(1, fade); ctx.fillStyle = '#000'; ctx.fillRect(0, 0, this.outW, this.outH); ctx.restore(); }
  }

  private drawWatermark(ctx: CanvasRenderingContext2D): void {
    const img = this.wordmark;
    const ar = img && img.naturalHeight ? img.naturalWidth / img.naturalHeight : null;
    const wb = watermarkBox(this.outW, this.outH, ar);
    ctx.save();
    ctx.globalAlpha = 0.92;
    if (img && ar) { try { ctx.drawImage(img, wb.x, wb.y, wb.w, wb.h); } catch { /* ignore */ } }
    ctx.globalAlpha = 0.95;
    ctx.font = `700 ${wb.fontPx}px Georgia, "Times New Roman", serif`;
    ctx.textAlign = 'center'; ctx.textBaseline = 'alphabetic';
    ctx.shadowColor = 'rgba(0,0,0,0.75)'; ctx.shadowBlur = 6; ctx.shadowOffsetY = 1;
    ctx.fillStyle = '#f2d98c';
    ctx.fillText(REPLAY.WATERMARK, wb.textX, wb.textY);
    ctx.restore();
  }

  /** Stop recording and resolve the finished clip (or null if nothing was captured). */
  async finish(): Promise<Blob | null> {
    const rec = this.rec; this.recording = false;
    if (!rec) return null;
    const blob = await new Promise<Blob | null>((resolve) => {
      rec.onstop = () => resolve(this.chunks.length ? new Blob(this.chunks, { type: this.mime }) : null);
      try { rec.stop(); } catch { resolve(null); }
    });
    this.rec = null;
    return blob;
  }

  /** The output canvas (for an optional on-screen preview / debug). */
  canvas(): HTMLCanvasElement | null { return this.out; }
  mimeType(): string { return this.mime; }
}

/* --------------------------------- share / download --------------------------------- */

function extFor(mime: string): string { return mime.startsWith('video/mp4') ? 'mp4' : 'webm'; }
export function replayFilename(mime: string): string {
  const ts = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
  return `sea-of-robin-highlight-${ts}.${extFor(mime)}`;
}

/** Trigger a browser download of the clip. */
export function downloadReplay(blob: Blob, mime: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = replayFilename(mime);
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 4000);
}

/** Share the clip via the native sheet (Web Share API with a file) on mobile, else download. Returns how it went. */
export async function shareReplay(blob: Blob, mime: string, text: string): Promise<'shared' | 'downloaded' | 'failed'> {
  const nav = navigator as Navigator & { canShare?: (d: ShareData) => boolean; share?: (d: ShareData) => Promise<void> };
  try {
    if (typeof File !== 'undefined' && nav.canShare && nav.share) {
      const file = new File([blob], replayFilename(mime), { type: mime });
      const data: ShareData = { files: [file], title: 'Sea of Solana', text } as ShareData;
      if (nav.canShare(data)) { await nav.share(data); return 'shared'; }
    }
  } catch (e) {
    // AbortError = the user dismissed the sheet; treat as handled (don't force a download on cancel)
    if ((e as { name?: string }).name === 'AbortError') return 'shared';
  }
  try { downloadReplay(blob, mime); return 'downloaded'; } catch { return 'failed'; }
}

function clampByte(v: number): number { return v < 0 ? 0 : v > 255 ? 255 : v; }
